import type { IgfdPaymentPlan, Restaurant } from "api-types";
import type { User } from "firebase/auth";
import mixpanel from "mixpanel-browser";

import { mixpanelConfig, mixpanelToken } from "@dashboard/environment/mixpanel";
import type { ReduxState } from "@dashboard/types";

type SignedInUser = Pick<User, "uid" | "displayName" | "email">;
type Workspace = Pick<Restaurant, "uuid" | "name" | "env" | "featureFlags">;
type FeatureFlagKey = keyof ReduxState["dashboardApp"]["featureFlags"];

/**
 * Dashboard flags Mixpanel may turn on per workspace, alongside the admin
 * toggle in Tools. Each Mixpanel flag key is the same name.
 */
export const MIXPANEL_FLAGS: readonly FeatureFlagKey[] = [
  "showAdvancedProductFilters",
  "showMenuSectionGroups",
  "enableGuestMenuV2",
];

type Session = {
  started: boolean;
  user: SignedInUser | null;
  // The user whose admin check has returned; nothing is sent before then.
  checkedUid: string | null;
  isAdmin: boolean;
  workspace: Workspace | null;
  plan: IgfdPaymentPlan | undefined;
  workspaceIds: string[];
  // What the profiles were last updated with, to skip repeat updates.
  lastProfiles: string;
};

const session: Session = {
  started: false,
  user: null,
  checkedUid: null,
  isAdmin: false,
  workspace: null,
  plan: undefined,
  workspaceIds: [],
  lastProfiles: "",
};

const flags = {
  enabled: new Set<string>() as ReadonlySet<string>,
  listeners: new Set<() => void>(),
  // Flag requests run one after another, in the order they were made.
  queue: Promise.resolve(),
};

// The dashboard must keep working when Mixpanel is blocked or failing.
const ignoreErrors = (fn: () => void) => {
  try {
    fn();
  } catch {
    /* ignore */
  }
};

const filteringLevel = (workspace: Workspace) =>
  workspace.featureFlags?.find((flag) => flag.flagName === "menuFilteringLevel")
    ?.flagValue ?? "none";

// Shared by the Workspace profile and flag requests.
const workspaceProperties = (
  workspace: Workspace,
  plan: IgfdPaymentPlan | undefined = session.plan
) => ({
  workspace_environment: workspace.env,
  workspace_filtering_level: filteringLevel(workspace),
  ...(plan && {
    plan_manual_allergens: plan.allowManualAllergensAndPreferences,
    plan_ingredient_search: plan.ingredientSearch,
  }),
});

const workspaceProfile = (workspace: Workspace, plan?: IgfdPaymentPlan) => ({
  $name: workspace.name,
  ...workspaceProperties(workspace, plan),
});

// Workspace profiles go straight to Mixpanel's Groups API rather than the
// SDK, so workspaces admins create or open are included while admins stay
// opted out. The public token is enough.
const sendWorkspaceProfile = (uuid: string, change: object) => {
  if (!session.started) return;
  const data = JSON.stringify([
    {
      $token: mixpanelToken,
      $group_key: "workspace_id",
      $group_id: uuid,
      ...change,
    },
  ]);
  fetch(`${mixpanelConfig.api_host}/groups`, {
    method: "POST",
    body: new URLSearchParams({ data }),
    keepalive: true,
  }).catch(() => {});
};

// Keeps Workspace and operator profiles readable in Mixpanel, only when
// something changed. Admins update the Workspace profile but never get an
// operator profile.
const updateProfiles = () => {
  const { checkedUid, isAdmin, workspace, workspaceIds } = session;
  if (!checkedUid || !workspace) return;
  const profile = workspaceProfile(workspace);
  const profiles = JSON.stringify([checkedUid, profile, workspaceIds]);
  if (profiles === session.lastProfiles) return;
  session.lastProfiles = profiles;
  sendWorkspaceProfile(workspace.uuid, { $set: profile });
  if (!isAdmin) {
    ignoreErrors(() => mixpanel.people.set({ workspace_id: workspaceIds }));
  }
};

const setEnabledFlags = (enabled: ReadonlySet<string>) => {
  flags.enabled = enabled;
  // Every event says which flags were on, so releases can be compared.
  ignoreErrors(() => mixpanel.register({ enabled_flags: [...enabled] }));
  flags.listeners.forEach((listener) => listener());
};

// Runtime properties that flag rollouts in Mixpanel can target.
const flagContext = (uid: string, workspace: Workspace) => ({
  workspace_id: workspace.uuid,
  custom_properties: {
    ...workspaceProperties(workspace),
    user_id: uid,
    user_role: session.isAdmin ? "admin" : "manager",
    is_admin: session.isAdmin,
    user_workspace_ids: session.workspaceIds,
  },
});

const fetchEnabledFlags = async (context: ReturnType<typeof flagContext>) => {
  mixpanel.set_config({ flags: { context } });
  // Rejects when the request fails, so failures leave flags off.
  await mixpanel.flags.load_flags();
  const variants = mixpanel.flags.get_all_variants_sync();
  return new Set(
    MIXPANEL_FLAGS.filter((flag) => variants.get(flag)?.value === true)
  );
};

// Asks Mixpanel which flags are on for the current user and workspace.
// Admins are included: the request stores nothing, and opt-out still blocks
// their events and profiles. Old values are cleared first, and an answer is
// only used if the user and workspace are still the ones it was asked for.
const requestFlags = () => {
  if (flags.enabled.size) setEnabledFlags(new Set());
  const { checkedUid, workspace } = session;
  if (!checkedUid || !workspace) return;
  const context = flagContext(checkedUid, workspace);
  const stillCurrent = () =>
    session.checkedUid === checkedUid &&
    session.workspace?.uuid === workspace.uuid;
  flags.queue = flags.queue
    .then(() => (stillCurrent() ? fetchEnabledFlags(context) : undefined))
    .then((enabled) => {
      if (enabled && stillCurrent()) setEnabledFlags(enabled);
    })
    .catch(() => {});
};

const forgetAccount = () => {
  session.checkedUid = null;
  session.isAdmin = false;
  session.workspace = null;
  session.plan = undefined;
  session.workspaceIds = [];
  session.lastProfiles = "";
  ignoreErrors(() => {
    // Flags off first, so the reset does not request them again.
    mixpanel.set_config({ flags: false });
    mixpanel.reset();
  });
  requestFlags();
};

const identify = (user: SignedInUser) => {
  mixpanel.clear_opt_in_out_tracking();
  mixpanel.identify(user.uid);
  mixpanel.people.set({
    ...(user.displayName && { $name: user.displayName }),
    ...(user.email && { $email: user.email }),
    role: "manager",
    is_admin: false,
  });
};

export function initMixpanel() {
  if (!mixpanelToken) return;
  ignoreErrors(() => {
    mixpanel.init(mixpanelToken!, mixpanelConfig);
    session.started = true;
  });
}

/** Nothing is sent on sign-in; identification waits for the admin check. */
export function mixpanelAuthChanged(user: SignedInUser | null) {
  const previous = session.user;
  session.user = user;
  if (session.started && previous && previous.uid !== user?.uid) {
    forgetAccount();
  }
}

/** Admins are opted out; everyone else is identified by Firebase UID. */
export function mixpanelAdminResolved(uid: string, isAdmin: boolean) {
  const { user } = session;
  // Ignore a result for an account that is no longer signed in.
  if (!session.started || user?.uid !== uid) return;
  ignoreErrors(() =>
    isAdmin ? mixpanel.opt_out_tracking({ delete_user: false }) : identify(user)
  );
  session.checkedUid = uid;
  session.isAdmin = isAdmin;
  requestFlags();
  updateProfiles();
}

/**
 * The selected workspace, its plan and every workspace the user can open.
 * Flags are requested again only when the selected workspace changes.
 */
export function setMixpanelWorkspace(
  workspace: Workspace,
  workspaceIds: string[],
  plan?: IgfdPaymentPlan
) {
  const changed = workspace.uuid !== session.workspace?.uuid;
  session.workspace = workspace;
  session.workspaceIds = workspaceIds;
  session.plan = plan;
  // Every event belongs to the selected workspace.
  ignoreErrors(() => mixpanel.register({ workspace_id: workspace.uuid }));
  if (changed) requestFlags();
  updateProfiles();
}

export const subscribeMixpanelFlags = (listener: () => void) => {
  flags.listeners.add(listener);
  return () => {
    flags.listeners.delete(listener);
  };
};

export const isMixpanelFlagEnabled = (flag: FeatureFlagKey) =>
  flags.enabled.has(flag);

/** Gives a new workspace its profile straight away. */
export function mixpanelWorkspaceCreated(
  workspace: Workspace,
  plan?: IgfdPaymentPlan
) {
  sendWorkspaceProfile(workspace.uuid, {
    $set: workspaceProfile(workspace, plan),
  });
}

/**
 * Keeps Mixpanel in step with a deleted workspace. Prod profiles are marked
 * deleted so past reports keep the restaurant's details; demo and internal
 * ones are removed.
 */
export function deleteMixpanelWorkspace(uuid: string, env: string) {
  sendWorkspaceProfile(
    uuid,
    env === "prod"
      ? { $set: { deleted: true, deleted_at: new Date().toISOString() } }
      : { $delete: "" }
  );
}

/** Nothing is sent before the admin check, or for admins. */
export function trackDashboardEvent(name: string, properties?: object) {
  if (!session.checkedUid || session.isAdmin) return;
  ignoreErrors(() => mixpanel.track(name, properties));
}

const usedFeatures = new Set<FeatureFlagKey>();

/** Counts the first use of a flagged feature in each page load. */
export function trackFeatureUsed(flag: FeatureFlagKey) {
  if (!session.checkedUid || session.isAdmin || usedFeatures.has(flag)) return;
  usedFeatures.add(flag);
  trackDashboardEvent("Feature Used", { feature: flag });
}
